import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, MessageCircle, Mic, MicOff, RotateCcw, Send, ShieldAlert, ShieldCheck, UserRound, Volume2, X } from 'lucide-react';
import { useCapturePublicChatLead, useRequestPublicChatHandoff } from '@cdf/api-client-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CHAT_MESSAGE_MAX,
  streamChat,
  type ChatStreamEvent,
  type ChatTurn
} from '@/lib/chat-stream';
import { cn } from '@/lib/utils';
import { browserVoiceSupported, speakBrowserReply, startBrowserTranscription } from '@/lib/browser-voice';

/**
 * Floating AI chat widget for public pages. Session-scoped history
 * (sessionStorage), streamed replies, and inline enquiry confirmation.
 */

interface ChatMessage extends ChatTurn {
  /** Set on the assistant message during which an enquiry was created. */
  enquiryCreated?: boolean;
  validation?: 'grounded' | 'low_confidence' | 'failed';
}

const STORAGE_KEY = 'circle-t-chat-history';
const SESSION_KEY = 'circle-t-chat-session';

const WELCOME: ChatMessage = {
  role: 'assistant',
  content: "Hi! I'm the Relationship Foundry assistant. Ask me about our services, or leave your details and the team will get back to you."
};

function loadStoredMessages(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [WELCOME];
    const parsed = JSON.parse(raw) as ChatMessage[];
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [WELCOME];
  } catch {
    return [WELCOME];
  }
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(loadStoredMessages);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) return stored;
    const created = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, created);
    return created;
  });
  const [voiceMode, setVoiceMode] = useState(false);
  const [listening, setListening] = useState(false);
  const [captureRequested, setCaptureRequested] = useState(false);
  const [lead, setLead] = useState({ name: '', email: '', phone: '', account: '', need: '' });
  const [handoffRequested, setHandoffRequested] = useState(false);
  const stopListeningRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const captureLead = useCapturePublicChatLead({ mutation: { onSuccess: () => setCaptureRequested(false) } });
  const handoff = useRequestPublicChatHandoff({ mutation: { onSuccess: () => setHandoffRequested(true) } });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-40)));
    } catch {
      // Storage full/unavailable — chat still works for the session.
    }
  }, [messages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open, streaming]);

  useEffect(() => () => {
    stopListeningRef.current?.();
    window.speechSynthesis?.cancel();
  }, []);

  async function send(text: string) {
    const message = text.trim();
    if (!message || streaming) return;
    setError(null);
    setFailedMessage(null);
    setInput('');

    // Send all non-empty turns (including the welcome message) so the model
    // sees the same conversation the visitor sees.
    const history: ChatTurn[] = messages
      .filter((m) => m.content.trim() !== '')
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, { role: 'user', content: message }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    let spokenReply = '';
    const handleEvent = (event: ChatStreamEvent) => {
      if (event.type === 'session') {
        setSessionId(event.sessionId);
        sessionStorage.setItem(SESSION_KEY, event.sessionId);
      }
      if (event.type === 'capture_requested') setCaptureRequested(true);
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (!last || last.role !== 'assistant') return prev;
        if (event.type === 'delta') {
          spokenReply += event.text;
          next[next.length - 1] = { ...last, content: last.content + event.text };
        } else if (event.type === 'enquiry_created') {
          next[next.length - 1] = { ...last, enquiryCreated: true };
        } else if (event.type === 'validation') {
          next[next.length - 1] = { ...last, validation: event.status };
        }
        return next;
      });
      if (event.type === 'error') {
        setError(event.message);
      }
    };

    try {
      await streamChat({ message, history, sessionId, mode: voiceMode ? 'voice' : 'chat' }, handleEvent);
      if (voiceMode && spokenReply.trim()) speakBrowserReply(spokenReply);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong — please try again.');
      setFailedMessage(message);
      // Drop the empty assistant placeholder if nothing streamed.
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        return last && last.role === 'assistant' && last.content === '' ? prev.slice(0, -1) : prev;
      });
    } finally {
      setStreaming(false);
    }
  }

  function toggleListening() {
    if (listening) {
      stopListeningRef.current?.();
      stopListeningRef.current = null;
      setListening(false);
      return;
    }
    setListening(true);
    stopListeningRef.current = startBrowserTranscription(
      (transcript) => {
        setInput(transcript);
        void send(transcript);
      },
      () => {
        stopListeningRef.current = null;
        setListening(false);
      }
    );
  }

  function retry() {
    if (!failedMessage) return;
    // Remove the failed user turn so it is not duplicated in history.
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      return last && last.role === 'user' && last.content === failedMessage ? prev.slice(0, -1) : prev;
    });
    void send(failedMessage);
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3">
      {open ? (
        <div className="flex h-[28rem] w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border bg-background shadow-lg">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Relationship Foundry Website Agent</p>
              <p className="text-xs text-muted-foreground">Website Agent — answers in seconds</p>
            </div>
            <div className="flex items-center gap-1">
              <Button variant={voiceMode ? 'secondary' : 'ghost'} size="icon" aria-label={voiceMode ? 'Disable voice mode' : 'Enable voice mode'} disabled={!browserVoiceSupported()} onClick={() => setVoiceMode((value) => !value)}>
                <Volume2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Close chat" onClick={() => setOpen(false)}><X className="h-4 w-4" /></Button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {messages.map((message, index) => (
              <div key={index} className={cn('flex flex-col', message.role === 'user' ? 'items-end' : 'items-start')}>
                {(message.content !== '' || !streaming || index !== messages.length - 1) && (
                  <div
                    className={cn(
                      'max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
                      message.role === 'user' ? 'bg-foreground text-background' : 'bg-muted'
                    )}
                  >
                    {message.content}
                  </div>
                )}
                {message.role === 'assistant' && message.content === '' && streaming && index === messages.length - 1 && (
                  <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Thinking…
                  </div>
                )}
                {message.enquiryCreated && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Enquiry sent to the Relationship Foundry team
                  </p>
                )}
                {message.role === 'assistant' && message.validation && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    {message.validation === 'grounded' ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
                    {message.validation === 'grounded' ? 'Checked against Relationship Foundry knowledge' : 'Safe fallback used'}
                  </p>
                )}
              </div>
            ))}
            {captureRequested && (
              <form className="space-y-2 rounded-md border p-3" onSubmit={(event) => {
                event.preventDefault();
                captureLead.mutate({ data: { sessionId, name: lead.name, email: lead.email, phone: lead.phone || null, account: lead.account || null, need: lead.need } });
              }}>
                <p className="text-xs font-medium">Let the team follow up</p>
                <div className="grid grid-cols-2 gap-2"><Input required placeholder="Name" value={lead.name} onChange={(event) => setLead((value) => ({ ...value, name: event.target.value }))} /><Input required type="email" placeholder="Email" value={lead.email} onChange={(event) => setLead((value) => ({ ...value, email: event.target.value }))} /></div>
                <div className="grid grid-cols-2 gap-2"><Input placeholder="Phone (optional)" value={lead.phone} onChange={(event) => setLead((value) => ({ ...value, phone: event.target.value }))} /><Input placeholder="Account (optional)" value={lead.account} onChange={(event) => setLead((value) => ({ ...value, account: event.target.value }))} /></div>
                <Input required placeholder="What do you need?" value={lead.need} onChange={(event) => setLead((value) => ({ ...value, need: event.target.value }))} />
                <Button type="submit" size="sm" disabled={captureLead.isPending}>Share details</Button>
              </form>
            )}
            {handoffRequested && <p className="rounded-md border px-3 py-2 text-xs">A human follow-up has been flagged for the team. Share your details above so they can contact you.</p>}
            {error && (
              <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs">
                <span>{error}</span>
                {failedMessage && (
                  <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={retry}>
                    <RotateCcw className="h-3 w-3" />
                    Retry
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="border-t p-3">
            <div className="mb-2 flex items-center justify-between">
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={handoff.isPending || handoffRequested} onClick={() => handoff.mutate({ data: { sessionId, reason: 'Visitor selected Talk to a human.' } })}><UserRound className="h-3.5 w-3.5" />Talk to a human</Button>
              {voiceMode && <span className="text-xs text-muted-foreground">Voice replies on</span>}
            </div>
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void send(input);
            }}
          >
            <Input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask about Relationship Foundry…"
              maxLength={CHAT_MESSAGE_MAX}
              disabled={streaming}
              aria-label="Chat message"
            />
            {voiceMode && (
              <Button type="button" variant={listening ? 'secondary' : 'outline'} size="icon" disabled={streaming} aria-label={listening ? 'Stop listening' : 'Speak message'} onClick={toggleListening}>
                {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
            )}
            <Button type="submit" size="icon" disabled={streaming || input.trim() === ''} aria-label="Send message">
              <Send className="h-4 w-4" />
            </Button>
          </form>
          </div>
        </div>
      ) : null}

      <Button size="icon" className="h-12 w-12 rounded-full shadow-lg" aria-label={open ? 'Hide chat' : 'Chat with us'} onClick={() => setOpen((v) => !v)}>
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </Button>
    </div>
  );
}
